import { _callBackend } from '@/_api/api';
import { fileUpload } from '@/_api/client-upload';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import { PriorYearItem } from '@/_types/models/priorYearItem';
import type { Company } from '@/_types/models/company';
import { getValidYearNumber } from '@/_utils/validations';

interface PriorYearItemAttrs {
  id?: string | number;
  companyId: string | number;
  doNotRollover?: boolean;
  name?: string;
  year?: string | number;
}

export const addPriorYearItem = async ({
  companyId,
  name,
  year,
  doNotRollover,
  token,
}: APIRequest<PriorYearItemAttrs>): APIResponsePromise =>
  await _callBackend(
    '/prior_year_items/add_prior_year_item',
    'POST',
    {
      company_id: companyId,
      name,
      do_not_rollover: doNotRollover,
      year: getValidYearNumber(year),
    },
    token,
  );

export const deletePriorYearItem = async ({
  id,
  companyId,
  token,
}: APIRequest<PriorYearItemAttrs>): APIResponsePromise =>
  await _callBackend(
    `/prior_year_items/${id}/delete_prior_year_item`,
    'POST',
    { company_id: companyId },
    token,
  );

export const getPriorYearItems = async ({
  companyId,
  token,
}: APIRequest<PriorYearItemAttrs>): APIResponsePromise<Array<PriorYearItem>> =>
  await _callBackend(
    `/prior_year_items/get_prior_year_items?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

export const updatePriorYearItem = async ({
  id,
  companyId,
  doNotRollover,
  name,
  year,
  token,
}: APIRequest<PriorYearItemAttrs>): APIResponsePromise =>
  await _callBackend(
    `/prior_year_items/${id}/update_prior_year_item`,
    'POST',
    {
      company_id: companyId,
      name,
      do_not_rollover: doNotRollover,
      year: getValidYearNumber(year),
    },
    token,
  );

export const uploadPriorYearExport = async ({
  companyId,
  file,
  documentName,
  documentType,
  year,
  token,
}: APIRequest<
  PriorYearItemAttrs & {
    file: File;
    documentName: string;
    documentType: string;
  }
>): APIResponsePromise => {
  if (!year) return Promise.reject(new Error('missing year'));

  const path: string = `/${year}/Tax Returns and Tax Organizers (STAFF ONLY)`;

  // Get a temporary upload link
  const { upload_link, id } = await _callBackend(
    `/integrations/get_temporary_upload_link?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

  // Upload file
  const url = new URL(upload_link);
  const isDropbox: boolean = url.hostname.includes('dropbox');

  await fileUpload(upload_link, isDropbox, file);

  return await _callBackend(
    '/integrations/process_document',
    'POST',
    {
      id,
      document_name: documentName,
      company_id: companyId,
      path,
      document_type: documentType,
      tax_year: getValidYearNumber(year),
    },
    token,
  );
};

export const getPriorYearScheduleLineItems = async ({
  companyId,
  priorYearItemId,
  token,
}: APIRequest<{
  priorYearItemId: string | number;
  companyId: number;
}>): APIResponsePromise =>
  await _callBackend(
    `/prior_year_items/get_prior_year_schedule_line_items?company_id=${companyId}&prior_year_item_id=${priorYearItemId}`,
    'GET',
    null,
    token,
  );

export const getPriorYearScheduleLineItemsByPriorYearId = async ({
  priorYearItemId,
  token,
}: APIRequest<{ priorYearItemId: string | number }>): APIResponsePromise =>
  await _callBackend(
    `/prior_year_items/get_prior_year_schedule_line_items_by_prior_year_id?prior_year_item_id=${priorYearItemId}`,
    'GET',
    null,
    token,
  );

export const updatePriorYearScheduleLineItem = async ({
  companyId,
  lineItemId,
  category,
  value,
  token,
}: APIRequest<{
  companyId: number;
  lineItemId: string | number;
  category: string;
  value: any;
}>): APIResponsePromise =>
  await _callBackend(
    '/prior_year_items/update_prior_year_schedule_line_item',
    'POST',
    {
      line_item_id: lineItemId,
      company_id: companyId,
      category,
      value,
    },
    token,
  );

export const updatePriorYearScheduleLineItemByItemId = async ({
  lineItemId,
  category,
  value,
  token,
}: APIRequest<{
  lineItemId: string | number;
  category: string;
  value: any;
}>): APIResponsePromise =>
  await _callBackend(
    '/prior_year_items/update_prior_year_schedule_line_item_by_item_id',
    'POST',
    {
      line_item_id: lineItemId,
      category,
      value,
    },
    token,
  );

export const addPriorYearScheduleLineItem = async ({
  companyId,
  priorYearItemId,
  category,
  value,
  token,
}: APIRequest<{
  companyId: number;
  priorYearItemId: string | number;
  category: string;
  value: any;
}>): APIResponsePromise =>
  await _callBackend(
    `/prior_year_items/${priorYearItemId}/add_prior_year_schedule_line_item`,
    'POST',
    {
      company_id: companyId,
      category,
      value,
    },
    token,
  );

export const addPriorYearScheduleLineItemWithoutCompany = async ({
  priorYearItemId,
  category,
  value,
  token,
}: APIRequest<{
  priorYearItemId: string | number;
  category: string;
  value: any;
}>): APIResponsePromise =>
  await _callBackend(
    `/prior_year_items/${priorYearItemId}/add_prior_year_schedule_line_item_without_company`,
    'POST',
    {
      category,
      value,
    },
    token,
  );

export const deletePriorYearScheduleLineItem = async ({
  companyId,
  lineItemId,
  token,
}: APIRequest<{
  companyId: number;
  lineItemId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/prior_year_items/delete_prior_year_schedule_line_item',
    'POST',
    {
      line_item_id: lineItemId,
      company_id: companyId,
    },
    token,
  );

export const deletePriorYearScheduleLineItemByItemId = async ({
  lineItemId,
  token,
}: APIRequest<{
  lineItemId: number;
}>): APIResponsePromise =>
  await _callBackend(
    '/prior_year_items/delete_prior_year_schedule_line_item_by_item_id',
    'POST',
    {
      line_item_id: lineItemId,
    },
    token,
  );

export const getRolloverItemsForFirm = async ({
  year,
  token,
}: APIRequest<{
  year: string;
}>): APIResponsePromise<{
  prior_year_items: Array<PriorYearItem>;
  number_of_entities_with_rollover_items_for_selected_year: number;
  total_number_of_rollover_items_for_selected_year: number;
}> =>
  await _callBackend(
    `/prior_year_items/get_prior_year_item_for_firm?year=${year}`,
    'GET',
    null,
    token,
  );

export const rolloverToNextYear = async ({
  year,
  token,
}: APIRequest<{
  year: string;
}>): APIResponsePromise<{
  message: string;
}> =>
  await _callBackend(
    `/prior_year_items/rollover_to_next_year?year=${year}`,
    'POST',
    null,
    token,
  );

// New combined endpoint with server-side pagination and filtering
export const getEntitiesWithRolloverItems = async ({
  year,
  page = 1,
  per_page = 10,
  search_term = '',
  has_rollover_items_only = false,
  token,
}: APIRequest<{
  year: string;
  page?: number;
  per_page?: number;
  search_term?: string;
  has_rollover_items_only?: boolean;
}>): APIResponsePromise<{
  entities: Array<
    Company & {
      rollover_items_count: number;
      rollover_items: Array<PriorYearItem>;
    }
  >;
  pagination: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_count: number;
  };
  summary: {
    total_rollover_items: number;
    entities_with_rollover_items_count: number;
    total_entities_count: number;
  };
}> =>
  await _callBackend(
    `/prior_year_items/get_entities_with_rollover_items?year=${year}&page=${page}&per_page=${per_page}&search_term=${encodeURIComponent(search_term)}&has_rollover_items_only=${has_rollover_items_only}`,
    'GET',
    null,
    token,
  );
